import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  resolveNotificationActionUrl,
  type NotificationVisualType,
  type SystemNotification,
} from '../../services/notifications';
import NotificationMessageBody from '../../components/notifications/NotificationMessageBody';
import { formatRelativeTimeAgo } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { notifyNotificationCountsUpdated } from '../../utils/notificationEvents';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { NOTIFICATIONS_RETURN } from '../task-case/taskCaseDetailsReturnNavigation';
import { isLegalOutcomeNotification } from '../../utils/notificationNavigation';

type TabKey = 'all' | 'unread';

const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;

function NotificationIcon({ type }: { type: NotificationVisualType }) {
  const cls =
    type === 'esc'
      ? 'ico-esc'
      : type === 'remind'
        ? 'ico-remind'
        : type === 'status'
          ? 'ico-status'
          : type === 'success'
            ? 'ico-success'
            : type === 'info'
              ? 'ico-info'
              : 'ico-comm';

  return (
    <div className={`nc-ico ${cls}`} aria-hidden="true">
      {type === 'esc' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ) : type === 'remind' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ) : type === 'status' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ) : type === 'success' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ) : type === 'info' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { applyViewModeIfNeeded } = useTaskCaseDetailsNavigation();
  const [tab, setTab] = useState<TabKey>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(5);
  const [listVersion, setListVersion] = useState(0);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [items, setItems] = useState<SystemNotification[]>([]);
  const [allCount, setAllCount] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState<number | undefined>(undefined);

  const reload = useCallback(() => setListVersion((v) => v + 1), []);

  const requestKey = `${tab}|${page}|${pageSize}|${listVersion}`;
  const loading = loadedForKey !== requestKey;

  useEffect(() => {
    const controller = new AbortController();

    void fetchNotifications({
      pageNumber: page,
      pageSize,
      unreadOnly: tab === 'unread',
      signal: controller.signal,
    }).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('notifications.loadFailed'), variant: 'error' });
        setItems([]);
        setListTotal(0);
        setTotalPages(1);
        setLoadedForKey(requestKey);
        return;
      }

      const unreadFromApi = res.data.unreadCount;
      const rawItems = res.data.items;
      let nextItems = rawItems;
      if (tab === 'unread') {
        nextItems = nextItems.filter((n) => !n.read);
      }

      setItems(nextItems);

      const totalAll = res.data.totalElements;
      setAllCount((prev) => {
        if (totalAll > 0) return totalAll;
        // API may return totalElements=0 once everything is read; keep the known total.
        if (rawItems.length > 0) return Math.max(prev, rawItems.length);
        if (prev > 0 && typeof unreadFromApi === 'number' && unreadFromApi === 0) return prev;
        return totalAll;
      });

      const unreadTotal =
        typeof unreadFromApi === 'number'
          ? unreadFromApi
          : tab === 'unread'
            ? nextItems.length
            : nextItems.filter((n) => !n.read).length;

      if (typeof unreadFromApi === 'number') {
        setUnreadCount(unreadFromApi);
      } else if (tab === 'all') {
        setUnreadCount(unreadTotal);
      }

      const activeListTotal = tab === 'unread' ? unreadTotal : totalAll;
      setListTotal(activeListTotal);
      setTotalPages(
        tab === 'unread'
          ? Math.max(1, Math.ceil(unreadTotal / pageSize) || 1)
          : res.data.totalPages,
      );

      if (typeof unreadFromApi === 'number') {
        notifyNotificationCountsUpdated({ unreadCount: unreadFromApi });
      }
      setLoadedForKey(requestKey);
    });

    return () => controller.abort();
  }, [requestKey, tab, page, pageSize, t]);

  const displayedUnreadCount = useMemo(() => {
    if (typeof unreadCount === 'number') return unreadCount;
    return items.filter((n) => !n.read).length;
  }, [unreadCount, items]);

  const pageStart = listTotal === 0 ? 0 : page * pageSize + 1;
  const pageEnd = Math.min((page + 1) * pageSize, listTotal);

  const handleMarkAllRead = async () => {
    if (markAllLoading || displayedUnreadCount === 0) return;
    setMarkAllLoading(true);
    const res = await markAllNotificationsRead();
    setMarkAllLoading(false);
    if (!res.success) {
      setFlashToast({ message: res.message ?? t('notifications.markAllFailed'), variant: 'error' });
      return;
    }
    setUnreadCount(0);
    notifyNotificationCountsUpdated({ unreadCount: 0 });
    reload();
  };

  const handleNotificationOpen = async (n: SystemNotification, actionPath?: string) => {
    if (markingId === n.id) return;

    const target =
      actionPath ?? (await resolveNotificationActionUrl(n));

    if (target) {
      const resolvedTarget = applyViewModeIfNeeded(target.startsWith('/') ? target : `/${target}`);
      const fromLegalOutcomeNotification = isLegalOutcomeNotification({
        notificationTypeCode: n.notificationTypeCode,
        scenario: n.scenario,
      });
      const go = () => {
        if (/^https?:\/\//i.test(resolvedTarget)) {
          window.location.assign(resolvedTarget);
        } else {
          nav(resolvedTarget.startsWith('/') ? resolvedTarget : `/${resolvedTarget}`, {
            state: fromLegalOutcomeNotification
              ? { fromLegalOutcomeNotification: true }
              : resolvedTarget.startsWith('/tasks/')
                ? { taskCaseReturn: NOTIFICATIONS_RETURN }
                : undefined,
          });
        }
      };
      // Defer navigation so the notification click cannot activate buttons on the destination page.
      window.setTimeout(go, 0);
    }

    if (!n.read) {
      setMarkingId(n.id);
      void markNotificationRead(n.id).then((res) => {
        setMarkingId(null);
        if (!res.success) {
          setFlashToast({ message: res.message ?? t('notifications.markReadFailed'), variant: 'error' });
          return;
        }
        setItems((prev) => prev.map((item) => (item.id === n.id ? { ...item, read: true } : item)));
        setUnreadCount((prev) => {
          const next = typeof prev === 'number' && prev > 0 ? prev - 1 : prev;
          if (typeof next === 'number') notifyNotificationCountsUpdated({ unreadCount: next });
          return next;
        });
      });
    }
  };

  const handleTabChange = (next: TabKey) => {
    setTab(next);
    setPage(0);
  };

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    const start = Math.max(0, Math.min(page - 2, totalPages - maxButtons));
    return Array.from({ length: maxButtons }, (_, i) => start + i);
  }, [page, totalPages]);

  return (
    <>
      <div className="bc">
        <span>{t('notifications.breadcrumb')}</span>
      </div>

      <div className="notif-toprow">
        <div className="notif-page-title">{t('notifications.title')}</div>
        <button
          className="mark-all-btn"
          type="button"
          disabled={markAllLoading || loading || displayedUnreadCount === 0}
          onClick={() => void handleMarkAllRead()}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {markAllLoading ? t('notifications.marking') : t('notifications.markAllRead')}
        </button>
      </div>

      <div className="notif-tabs">
        <button className={`nt ${tab === 'all' ? 'on' : ''}`} type="button" onClick={() => handleTabChange('all')}>
          {t('notifications.allTab', { count: allCount })}
        </button>
        <button className={`nt ${tab === 'unread' ? 'on' : ''}`} type="button" onClick={() => handleTabChange('unread')}>
          {t('notifications.unreadTab', { count: displayedUnreadCount })}
        </button>
      </div>

      <div className="nc-wrap">
        {loading ? (
          <div className="nc" style={{ cursor: 'default' }}>
            <div className="nc-content" style={{ color: 'var(--t3)', fontSize: 13 }}>
              {t('notifications.loading')}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="nc" style={{ cursor: 'default' }}>
            <div className="nc-content" style={{ color: 'var(--t3)', fontSize: 13 }}>
              {tab === 'unread' ? t('notifications.noUnread') : t('notifications.none')}
            </div>
          </div>
        ) : (
          items.map((n) => (
              <div
                key={n.id}
                className={`nc ${n.read ? 'read' : 'unread'}${n.taskNavigable ? ' nc-navigable' : ''}`}
                role={n.taskNavigable ? 'button' : undefined}
                tabIndex={n.taskNavigable ? 0 : undefined}
                onClick={() => void handleNotificationOpen(n)}
                onKeyDown={(e) => {
                  if (!n.taskNavigable) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void handleNotificationOpen(n);
                  }
                }}
              >
                <NotificationIcon type={n.type} />
                <div className="nc-content">
                  <div className="nc-header">
                    <div className="nc-title">{n.title}</div>
                    <div className="nc-time">
                      {n.createdAt ? formatRelativeTimeAgo(n.createdAt) : n.createdAtLabel}
                    </div>
                  </div>
                  {n.body ? (
                    <NotificationMessageBody message={n.body} />
                  ) : null}
                </div>
              </div>
          ))
        )}
      </div>

      <div className="notif-pager">
        <div className="np-info">
          {listTotal === 0
            ? null
            : t('notifications.showingRange', { start: pageStart, end: pageEnd, total: listTotal, count: listTotal })}
        </div>
        <div className="np-right">
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>{t('common.rowsPerPage')}</span>
          <select
            className="rpp-sel"
            value={pageSize}
            disabled={loading}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <div className="npg">
            <button
              type="button"
              className={`npb ${page <= 0 ? 'dis' : ''}`}
              disabled={page <= 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {t('common.previous')}
            </button>
            {pageNumbers.map((pageIndex) => (
              <button
                key={pageIndex}
                type="button"
                className={`npb ${pageIndex === page ? 'on' : ''}`}
                disabled={loading}
                onClick={() => setPage(pageIndex)}
              >
                {pageIndex + 1}
              </button>
            ))}
            <button
              type="button"
              className={`npb ${page >= totalPages - 1 ? 'dis' : ''}`}
              disabled={page >= totalPages - 1 || loading}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
